import { useTranslation } from 'react-i18next';
import { useIsExportRender, useRequiredParams } from 'app/router/params';
import { useState, useRef, useCallback, memo } from 'react';

import { ReactEditor, useSlateSelector, useSlateStatic, RenderElementProps } from 'slate-react';
import { CustomEditor, PageElement } from 'slate/types/slate';
import { useEditorContext } from 'app/state/contexts/EditorContext';
import { createPortal } from 'react-dom';
import { refHandler } from 'shared/lib/helper-functions';
import { usePopper } from 'react-popper';
import { times } from 'ramda';
import { StructurePreview, Template } from 'app/components/StructurePreview';
import { Modal } from 'app/components';
import Area from './Area';
import { insertTemplate } from 'slate/extensions/page';
import { useEditorSearch } from 'app/router/params';
import { useDesignDraft } from 'app/api/documents/useDesignDraft';
import { useDocumentRevision, useSectionRevision, SectionEntry, PageArea } from 'entities/document';
import { useOverflowMarker } from './useOverflowMarker';
import { usePermissions } from 'app/api/permission-queries';
import { useUser } from 'app/state/contexts/UserContext';
import { useTemplates as useTemplatesQuery } from 'entities/template';
import { useEditorScope } from 'app/state/contexts/EditorScopeContext';
import { useEditorLocale } from 'app/api/documents/useEditorData';
import { useEditorDocument } from 'app/api/documents/useEditorData';

interface TemplateItem {
  revisionId: string;
  name: string;
  organization?: number | null;
  documentSuperId?: number | string | null;
  structure?: unknown;
  [key: string]: unknown;
}

interface PageDividerProps {
  index: number;
  pageRef: HTMLElement | null;
  placement: 'top' | 'bottom';
  readOnly?: boolean;
}

const PageDivider = ({ index, pageRef, placement, readOnly }: PageDividerProps) => {
  const { t } = useTranslation('doc-editor');

  const editor = useSlateStatic();
  const { documentId, sectionId } = useRequiredParams('documentId', 'sectionId');
  const { sectionPermissions: reduxSectionPermissions } = usePermissions(documentId);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [showNewPageModal, setShowNewPageModal] = useState(false);
  const { styles, attributes } = usePopper(pageRef, popperElement, {
    placement,
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: [],
        },
      },
    ],
  });

  const isSectionButtonDisabled = () => {
    return reduxSectionPermissions[sectionId] === 'COMMENT';
  };

  if (readOnly) {
    return <Portal />;
  }

  return (
    <Portal>
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div ref={setPopperElement} style={styles.popper} {...attributes.popper}>
          <div className="page-divider">
            {isSectionButtonDisabled() ? (
              <button disabled className="page-divider--button text-disabled">
                {t('page-divider.link.new-page')} <i className="fa-regular fa-file" />
              </button>
            ) : (
              <button className="page-divider--button" onClick={() => setShowNewPageModal(true)}>
                {t('page-divider.link.new-page')} <i className="fa-regular fa-file" />
              </button>
            )}
            {<LayoutPicker show={showNewPageModal} editor={editor} index={index} onClose={() => setShowNewPageModal(false)} />}
          </div>
        </div>
      </div>
    </Portal>
  );
};

const emptyExtra = (index: number) => (
  <div key={index} className="page-wrapper offset-page" contentEditable={false} style={{ userSelect: 'none' }}>
    <div className="page-content"></div>
  </div>
);

interface LayoutPickerProps {
  onClose: () => void;
  show: boolean;
  editor?: CustomEditor;
  index?: number;
}

export const LayoutPicker = ({ onClose, show, ...rest }: LayoutPickerProps) => {
  const { t } = useTranslation(['common', 'theme']);

  const { selectedOrganization } = useUser();
  const scopedDocumentId = useEditorScope()?.documentId;
  const { data: templates } = useTemplatesQuery({ organization: selectedOrganization, documentSuperId: scopedDocumentId, type: 'template', selected: true });
  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all');
  const { style, draftEntry } = useDesignDraft({ styleType: 'graphical-variables', parsed: true });
  const docVars = { ...((style.data?.content ?? {}) as Record<string, string>), ...((draftEntry?.style ?? {}) as Record<string, string>) };
  const aspectRatio = `${parseInt(docVars.pageWidth, 10) || 794} / ${parseInt(docVars.pageHeight, 10) || 1123}`;

  const filtered = ((templates || []) as TemplateItem[]).filter((page) => {
    const matchesSearch = !search || page.name.toLowerCase().includes(search.toLowerCase());
    const matchesScope =
      scopeFilter === 'all' ||
      (scopeFilter === 'global' && !page.organization && !page.documentSuperId) ||
      (scopeFilter === 'organization' && page.organization && !page.documentSuperId);
    return matchesSearch && matchesScope;
  });

  return (
    <Modal titleLabelKey="layout-picker:title" show={show} onClose={onClose} size="xl" scrollable>
      <div className="d-flex align-items-center gap-2 mb-3 sticky-top bg-blue-100 p-2 rounded">
        <input type="text" className="form-control" placeholder={t('common:search')} value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="form-select" style={{ width: 'auto' }} value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value)}>
          <option value="all">{t('common:all')}</option>
          <option value="global">{t('common:global')}</option>
          <option value="organization">{t('common:organization')}</option>
        </select>
      </div>
      {filtered.length === 0 ? (
        t('theme:structure.no-available')
      ) : (
        <div className="d-flex flex-wrap justify-content-start gap-r-2">
          {filtered.map((template) => (
            <Layout {...rest} onSelect={onClose} template={template} key={template.revisionId} aspectRatio={aspectRatio} />
          ))}
        </div>
      )}
    </Modal>
  );
};

interface LayoutProps {
  editor?: CustomEditor;
  index?: number;
  template: TemplateItem;
  onSelect: () => void;
  aspectRatio?: string;
}

const Layout = ({ editor, index, template, onSelect, aspectRatio }: LayoutProps) => {
  const handleChoseLayout = () => {
    if (!editor) return;
    insertTemplate(editor, template as { structure: unknown }, index ?? 0);
    onSelect();
  };
  return (
    <div className="mb-3">
      <StructurePreview template={template as Template} onClick={handleChoseLayout} aspectRatio={aspectRatio} inModal />
    </div>
  );
};

export const SectionName = () => {
  const revisionIdLeft = useEditorSearch().revisionL;
  const { documentId, sectionId } = useRequiredParams('documentId', 'sectionId');

  const { data: documentL } = useDocumentRevision(revisionIdLeft || undefined, revisionIdLeft ? undefined : documentId);
  const sectionRevisionId = documentL?.content?.sections?.find((section: SectionEntry) => section.superId === sectionId)?.revisionId;
  const { data: sectionL = {} } = useSectionRevision(sectionRevisionId);

  return <span className="page-divider-section-name">{(sectionL as { name?: string }).name}</span>;
};

const Portal = ({ children }: { children?: React.ReactNode }) => {
  const { editorContainerRef } = useEditorContext();
  if (editorContainerRef.current == null) return null;
  return createPortal(children, editorContainerRef.current);
};

const MemoizedArea = memo(({ pageId, areaConfig }: { pageId: string; areaConfig: PageArea | null | undefined }) => (
  <Area pageId={pageId} areaConfig={areaConfig} />
));

interface PageProps {
  attributes: RenderElementProps['attributes'];
  children: React.ReactNode;
  element: PageElement;
  withButtons?: boolean;
  readOnly?: boolean;
  extraOffset?: number;
}

const Page = ({ attributes, children, element, withButtons, readOnly, extraOffset = 0 }: PageProps) => {
  const { data: pDocument } = useEditorDocument();
  const locale = useEditorLocale();
  const [pageRef, setPageRef] = useState<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const index = useSlateSelector(
    useCallback((editor: CustomEditor) => ReactEditor.findPath(editor, element)[0], [element]),
    undefined,
    { deferred: true }
  );
  const isExportRender = useIsExportRender();
  useOverflowMarker(contentRef, containerRef, { enabled: !isExportRender });

  const pageCssClasses = element.class
    ? element.class
        .split(',')
        .map((c: string) => c.trim())
        .filter(Boolean)
        .join(' ')
    : '';
  return (
    <>
      {times(emptyExtra, extraOffset)}
      <div {...attributes} ref={refHandler(attributes, setPageRef as (ref: unknown) => void)} className="page-wrapper">
        {withButtons && !index ? <PageDivider readOnly={readOnly} index={index} pageRef={pageRef} placement="top" /> : null}
        {withButtons ? <PageDivider readOnly={readOnly} index={index + 1} pageRef={pageRef} placement="bottom" /> : null}
        <div
          className={`page-content xrp-template-${element.templateSuperId || ''}${pageCssClasses ? ' ' + pageCssClasses : ''}${
            locale ? ' locale-' + locale.toLowerCase() : ''
          }`.trim()}
          id={`id-${element.uuid}`}
          ref={containerRef}
          style={{ position: 'relative' }}
        >
          <div
            contentEditable={false}
            className={`area-header${element.areas?.header?.className ? ' ' + element.areas.header.className : ''}`}
            style={element.areas?.header?.hidden || pDocument?.content?.header === null ? { display: 'none' } : undefined}
          >
            <MemoizedArea pageId={element.uuid} areaConfig={pDocument.content?.header} />
          </div>
          <div className={`area-content${element.areas?.content?.className ? ' ' + element.areas.content.className : ''}`} ref={contentRef}>
            {children}
          </div>
          <div
            contentEditable={false}
            className={`area-footer${element.areas?.footer?.className ? ' ' + element.areas.footer.className : ''}`}
            style={element.areas?.footer?.hidden || pDocument?.content?.footer === null ? { display: 'none' } : undefined}
          >
            <MemoizedArea pageId={element.uuid} areaConfig={pDocument.content?.footer} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Page;
